<template>
  <section
    class="intro-story"
    :style="sectionStyle"
    aria-label="StoReel interactive story preview"
  >
    <div class="intro-story__background" :style="backgroundStyle" aria-hidden="true" />
    <div class="intro-story__scrim" aria-hidden="true" />

    <div class="intro-story__video-frame" :style="videoStyle">
      <video :src="content.videoSrc" autoplay muted loop playsinline preload="auto" />
    </div>

    <h2 class="intro-story__title" :style="titleStyle">{{ content.title }}</h2>
    <p class="intro-story__description" :style="descriptionStyle">
      {{ content.description }}
    </p>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { CSSProperties } from 'vue'

import type { DesignBox, IntroStorySection } from '../../data/home2026'

const props = defineProps<{
  content: IntroStorySection
  scaledPx: (value: number) => string
}>()

const boxStyle = (box: DesignBox): CSSProperties => ({
  left: props.scaledPx(box.x),
  top: props.scaledPx(box.y),
  width: props.scaledPx(box.w),
  height: props.scaledPx(box.h),
})

const centerBoxStyle = (box: DesignBox): CSSProperties => ({
  left: props.scaledPx(box.x),
  top: props.scaledPx(box.y),
  width: props.scaledPx(box.w),
  minHeight: props.scaledPx(box.h),
})

const sectionStyle = computed<CSSProperties>(() => boxStyle(props.content.box))

const backgroundStyle = computed<CSSProperties>(() => ({
  backgroundImage: `url("${props.content.backgroundSrc}")`,
}))

const videoStyle = computed<CSSProperties>(() => ({
  ...boxStyle(props.content.videoBox),
  borderRadius: props.scaledPx(10),
}))

const titleStyle = computed<CSSProperties>(() => ({
  ...centerBoxStyle(props.content.titleBox),
  fontSize: props.scaledPx(50),
  lineHeight: props.scaledPx(70),
}))

const descriptionStyle = computed<CSSProperties>(() => ({
  ...centerBoxStyle(props.content.descriptionBox),
  fontSize: props.scaledPx(30),
  lineHeight: props.scaledPx(42),
}))
</script>

<style scoped>
.intro-story {
  position: absolute;
  z-index: 2;
  overflow: hidden;
  background: #000;
  color: #fff;
  font-family: 'Alibaba PuHuiTi 3.0', AlibabaPuHuiTi_3_55_Regular, PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-synthesis: none;
  pointer-events: none;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricprecision;
}

.intro-story__background,
.intro-story__scrim {
  position: absolute;
  inset: 0;
}

.intro-story__background {
  background-position: center;
  background-size: cover;
  filter: blur(0.2rem);
  transform: scale(1.04);
}

.intro-story__scrim {
  background: rgb(0 0 0 / 50%);
}

.intro-story__video-frame {
  position: absolute;
  overflow: hidden;
  border-radius: var(--video-radius);
  background: #050505;
  box-shadow: 0 18px 38px rgb(0 0 0 / 30%);
  contain: layout paint style;
}

.intro-story__video-frame video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.intro-story__title,
.intro-story__description {
  position: absolute;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  transform: translateY(-50%);
}

.intro-story__title {
  font-family: AlibabaPuHuiTi_3_65_Medium, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-weight: 500;
  font-synthesis: none;
  letter-spacing: 0;
  white-space: normal;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricprecision;
}

.intro-story__description {
  font-family: AlibabaPuHuiTi_3_55_Regular, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-weight: 400;
  font-synthesis: none;
  letter-spacing: 0;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricprecision;
}
</style>
